import { useEffect, useState, useCallback, useRef } from 'react';
import { CognitiveSearchResultModel, ImageLibrarySearchResult } from '@/types';
import tailwindConfig from '@/tailwind.screens';
import { ProgressCircular } from '@/components';
import ImageLibraryResultGridColumn from './ImageLibraryResultGridColumn';

type ImageLibraryResultGridType = {
  result: CognitiveSearchResultModel<ImageLibrarySearchResult>;
  onSelectImage: (imageLibrary: ImageLibrarySearchResult) => void;
  selectedIds: number[];
  showSaveToScrapbook?: boolean;
  showDownloadBtn?: boolean;
  onLoadMore: () => void;
  loading?: boolean;
};

const ImageLibraryResultGrid = (data: ImageLibraryResultGridType) => {
  const {
    result: { results: imageList, totalResults },
    onSelectImage,
    selectedIds,
    showSaveToScrapbook,
    showDownloadBtn,
    onLoadMore,
    loading,
  } = data;

  const [groupedImagesList, setGroupedImagesList] = useState<ImageLibrarySearchResult[][]>([]);
  const [gridColumns, setGridColumns] = useState(0);

  const [isSentinelVisible, setIsSentinelVisible] = useState(false);
  const [allCurrentImagesLoaded, setAllCurrentImagesLoaded] = useState(false);

  const columsContainer = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const loadedCountRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= parseInt(tailwindConfig.screens.xl, 10)) {
        setGridColumns(5);
      } else if (window.innerWidth >= parseInt(tailwindConfig.screens.lg, 10)) {
        setGridColumns(4);
      } else if (window.innerWidth >= parseInt(tailwindConfig.screens.md, 10)) {
        setGridColumns(3);
      } else {
        setGridColumns(2);
      }
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (entry.isIntersecting) {
          setIsSentinelVisible(true);
        } else {
          setIsSentinelVisible(false);
        }
      },
      {
        // root: columsContainer.current?.parentElement?.parentElement, // scroll container
        rootMargin: '200px', // preload before exact bottom
        threshold: 0,
      },
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, []);

  useEffect(() => {
    loadedCountRef.current = new Set();
    setAllCurrentImagesLoaded(false);
    setGroupedImagesList([]);
  }, [gridColumns]);

  useEffect(() => {
    if (imageList.length === 0) {
      loadedCountRef.current = new Set();
      setGroupedImagesList([]);
    }
    setAllCurrentImagesLoaded(false);
  }, [imageList.length]);

  useEffect(() => {
    if (
      isSentinelVisible &&
      !loading &&
      imageList.length > 0 &&
      allCurrentImagesLoaded &&
      loadedCountRef.current.size === imageList.length &&
      loadedCountRef.current.size < totalResults
    ) {
      onLoadMore();
    }
  }, [isSentinelVisible, loading, allCurrentImagesLoaded, imageList.length, totalResults, onLoadMore]);

  useEffect(() => {
    if (gridColumns === 0 || imageList.length === 0) return;

    setGroupedImagesList((prev) => {
      // Initialize columns if empty
      const result = prev.length === 0 ? Array.from({ length: gridColumns }, () => []) : prev.map((col) => [...col]);

      // Collect existing IDs
      const existingIds = new Set(result.flat().map((item) => item.index));

      // Filter only new items
      const newItems = imageList.filter((item) => !existingIds.has(item.index));

      // Distribute only new items
      newItems.forEach((item, index) => {
        const columnIndex = index % gridColumns;
        result[columnIndex].push(item);
      });

      return result;
    });
  }, [gridColumns, imageList]);

  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement, Event>, index: number) => {
      if (e.currentTarget.currentSrc.startsWith('data:')) return;
      const set = loadedCountRef.current;
      if (!set.has(index)) {
        set.add(index);

        if (set.size === imageList.length) {
          setAllCurrentImagesLoaded(true);
        }
      }
    },
    [imageList.length],
  );

  const balanceImageColumns = (runCount = 0): void => {
    const container = columsContainer.current;
    if (!container || runCount >= 1 || totalResults <= 10) return;

    const columns = Array.from(container.children) as HTMLElement[];
    if (columns.length < 2) return;

    const heights = columns.map((col) => col.offsetHeight);

    if (Math.max(...heights) - Math.min(...heights) < 200) return;

    let maxIndex = 0;
    let minIndex = 0;

    for (let i = 1; i < heights.length; i++) {
      if (heights[i] > heights[maxIndex]) maxIndex = i;
      if (heights[i] < heights[minIndex]) minIndex = i;
    }

    if (maxIndex === minIndex) return;

    setGroupedImagesList((prev) => {
      const sourceColumn = prev[maxIndex];
      if (!sourceColumn?.length) return prev;

      const next = prev.map((group) => [...group]);
      const movedItem = next[maxIndex].pop();

      if (movedItem !== undefined) {
        next[minIndex].push(movedItem);
      }

      return next;
    });

    setTimeout(() => {
      balanceImageColumns(runCount + 1);
    }, 300);
  };

  useEffect(() => {
    if (imageList.length > 0 && allCurrentImagesLoaded) {
      balanceImageColumns(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCurrentImagesLoaded]);

  return (
    <div className="w-full flex-1 px-3 md:px-0">
      <div className="h-full w-full">
        <div ref={columsContainer} className="flex items-start gap-4">
          {groupedImagesList.map((images, index) => (
            <ImageLibraryResultGridColumn
              key={index}
              imageList={images}
              onSelectImage={onSelectImage}
              onImageLoad={onImageLoad}
              selectedIds={selectedIds}
              showSaveToScrapbook={showSaveToScrapbook}
              showDownloadBtn={showDownloadBtn}
            />
          ))}
        </div>

        <div className="flex h-24 w-full items-center justify-center py-6">
          {loading && <ProgressCircular indeterminate size={50} width={4} colorClassName="stroke-sky" />}
        </div>
        {totalResults <= imageList.length && (
          <div className="w-full text-center">{totalResults === 0 ? 'No results.' : 'No more results.'}</div>
        )}
        {/* Sentinel */}
        <div ref={loadMoreRef} className="h-10 w-full" />
      </div>
    </div>
  );
};

export default ImageLibraryResultGrid;
